import { FC, useState } from "react";
import * as _ from "lodash";
import { useSelector } from "react-redux";
import { CSVLink } from "react-csv";

import { Tabs, CalendarOutlined, Typography, Row, Col, Button, DownloadOutlined } from "@ctra/components";
import { useTranslation, Enterprise as Content } from "@ctra/i18n";
import { Enterprise, EnterpriseAppState, GenericInsightEntity, Insights } from "@ctra/api";
import { AjinomotoBloodTestInsight, InsightListContext, useInsight, testIds } from "@insights";

import { BloodTestTable } from "./components";
import styles from "./AjinomotoBloodScreeningSchedule.module.less";
import { Nullable } from "@ctra/utils";

const { TabPane } = Tabs;
const { Paragraph, Text } = Typography;

/**
 * Blood screening schedule using child insights
 * @return {React.ReactNode}
 * @constructor
 */
export const AjinomotoBloodScreeningSchedule: FC = () => {
  const [currentTab, setCurrentTab] = useState<string>("thisWeek");
  const { t } = useTranslation();

  const {
    insight: { id }
  } = useInsight<AjinomotoBloodTestInsight>();

  const {
    insights: {
      extensions: {
        bloodScreeningSchedule: { tabs, title, description, exportLabel }
      }
    }
  } = Content;

  /**
   * Query for pulling child insights
   * @type {{filter: {and: {parentGenericInsightID: number}}, expand: Array<string>, orderBy: string}}
   */
  const query = {
    orderBy: "endEpoch desc",
    expand: Insights.presets.expandDefaults(),
    filter: {
      and: {
        parentGenericInsightID: id
      }
    }
  };

  /**
   * Pick the current insight list (may be null until is is loaded from the server)
   * @type {Nullable<Array<GenericInsightEntity>>}
   */
  const insightList = useSelector<EnterpriseAppState, Nullable<Array<GenericInsightEntity>>>((state) =>
    Enterprise.entities.getInsightList(state, { list: `bloodScreening-${id}-${currentTab}` })
  );

  return (
    <div data-testid={testIds.extensions.bloodScreeningSchedule.wrapper}>
      <Row justify="space-between">
        <Col>
          <Paragraph className={styles.NoMargin}>
            <Text strong>{t<string>(title)}</Text>
            <br />
            <Text>{t<string>(description)}</Text>
          </Paragraph>
        </Col>
        <Col>
          {insightList && insightList.length ? (
            <CSVLink
              separator=";"
              className="ant-btn"
              role="button"
              filename={`bloodScreening-${id}-${currentTab}.csv`}
              data={_.map(insightList as NonNullable<Array<GenericInsightEntity>>, ({ metadata }) =>
                _.mapValues(metadata, (v) => (_.isPlainObject(v) ? JSON.stringify(v) : v))
              )}
            >
              <DownloadOutlined />
              <span>{t<string>(exportLabel)}</span>
            </CSVLink>
          ) : (
            <Button disabled icon={<DownloadOutlined />}>
              {t<string>(exportLabel)}
            </Button>
          )}
        </Col>
      </Row>
      <Tabs className={styles.Tabs} onTabClick={(tab) => setCurrentTab(tab)}>
        <TabPane
          key="thisWeek"
          tab={
            <span>
              <CalendarOutlined />
              {t<string>(tabs.thisWeek)}
            </span>
          }
        >
          <InsightListContext.Provider
            query={_.merge({}, query, {
              filter: {
                insightMetadata: { contains: "thisWeek" }
              }
            })}
            list={`bloodScreening-${id}-thisWeek`}
          >
            <BloodTestTable />
          </InsightListContext.Provider>
        </TabPane>
        <TabPane
          key="nextWeek"
          tab={
            <span>
              <CalendarOutlined />
              {t<string>(tabs.nextWeek)}
            </span>
          }
        >
          <InsightListContext.Provider
            query={_.merge({}, query, {
              filter: {
                insightMetadata: { contains: "nextWeek" }
              }
            })}
            list={`bloodScreening-${id}-nextWeek`}
          >
            <BloodTestTable />
          </InsightListContext.Provider>
        </TabPane>
      </Tabs>
    </div>
  );
};
